import { makeStyles } from '@mui/styles';
import { Grid, LinearProgress, Peek, Typography } from 'apollo-react';
import { primary } from 'apollo-react/colors';
import StarOutline from 'apollo-react-icons/StarOutline';
import StarSolid from 'apollo-react-icons/StarSolid';
import _ from 'lodash';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useFormSelector } from '../../custom-hooks';
import { createPreview } from '../../form-designer/actions/form-manager.actions';
import DynamicField from '../../form-designer/DynamicField';
import { makeGetEntityPropertyValue } from '../../form-designer/selectors/common.selectors';
import { getHeaderInfo } from '../../form-designer/selectors/form-manager.selectors';
import { getHeaderTitleText } from '../../form-designer/utils/common.utils';
import { getLinkPreviewResultsKey } from '../../reducers/LinkPreviewResultsReducer';
import { getCachedLinkPreviewData, getIsFavorite } from '../../selectors/PreviewLinkSelectors';
import CustomLoader from '../CustomLoader';

const useStyles = makeStyles({
  root: { width: 300 },
  header: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: 5,
  },
  title: {
    paddingTop: 10,
    paddingLeft: 8,
    fontWeight: 'bold',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    '& span > span': {
      width: '50%',
    },
  },
});

const Preview = ({ to }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [progress, setProgress] = useState(0);

  const qsParams = qs.parse(to?.search) || {};
  const linkPreviewKey = getLinkPreviewResultsKey({
    formKey: qsParams.formKey,
    id: qsParams.itmId,
  });

  const cachedData = useSelector((state) =>
    getCachedLinkPreviewData(state, {
      linkPreviewKey,
    })
  );

  const headerElement = useFormSelector(getHeaderInfo, { pathname: to?.pathname });
  const recordNumber = useFormSelector(makeGetEntityPropertyValue, {
    propertyName: 'RecordNumberColumn',
  });
  const revisionNumber = useFormSelector(makeGetEntityPropertyValue, {
    propertyName: 'RecordRevisionColumn',
  });

  const isFavorite = useSelector((state) =>
    getIsFavorite(state, {
      userKey: qsParams.userKey,
      entityType: qsParams.entityType,
      recordNumber,
      revisionNumber,
    })
  );

  const headerTitleText = getHeaderTitleText({
    userKey: qsParams.userKey,
    recordNumber,
    revisionNumber,
  });

  const fetchData = () => {
    dispatch(
      createPreview({
        entityType: qsParams.entityType,
        containerWorkflowKey: qsParams.containerWorkflowKey,
        workflowKey: qsParams.workflowKey,
        stepId: qsParams.stepId,
        itmId: qsParams.itmId,
        workflowId: qsParams.workflowId,
        parentWorkflowId: qsParams.parentWorkflowId,
        formKey: qsParams.formKey,
        formId: qsParams.formId,
        entityName: qsParams.entityName,
      })
    );
  };

  useEffect(() => {
    if (cachedData) {
      return;
    }

    const timer = setInterval(() => {
      setProgress((prev) => {
        const nextProgress = prev + 20;

        if (nextProgress >= 100) {
          clearInterval(timer);
        }

        return nextProgress;
      });
    }, 100);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cachedData) {
      return;
    }

    if (progress >= 100) {
      fetchData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, cachedData]); // TODO: why not JSON.stringify?

  useEffect(() => {
    if (cachedData && !cachedData.isFetching && !cachedData.errors?.length) {
      fetchData();
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(cachedData)]); // TODO: why JSON.stringify? // TODO: JSON.stringify(cachedData) is too heavy

  return (
    <Peek
      open
      placement="bottom"
      content={
        <div className={classes.root}>
          {cachedData?.errors ? (
            <Typography variant="title2" gutterBottom>
              {cachedData?.errors[0].errorMessage}
            </Typography>
          ) : cachedData?.isFetching ? (
            <CustomLoader />
          ) : cachedData && !_.isEmpty(headerElement) ? (
            <>
              <div className={classes.header}>
                {isFavorite ? (
                  <StarSolid fontSize="small" style={{ fill: primary }} />
                ) : (
                  <StarOutline fontSize="small" />
                )}
                <Typography variant="title2" gutterBottom className={classes.title}>
                  {headerTitleText}
                </Typography>
              </div>
              {!_.isEmpty(headerElement) && (
                <Grid item xs={12} className={classes.content}>
                  {headerElement.childrenNodes?.map((key) => (
                    <DynamicField key={key} id={key} isDetail />
                  ))}
                </Grid>
              )}
            </>
          ) : (
            <LinearProgress variant="determinate" value={progress} />
          )}
        </div>
      }
    />
  );
};

export default Preview;
